import React from 'react';
import { motion } from 'framer-motion';
import StarNode from './StarNode';

const SolarSystem = ({ user, isDetailView = false }) => {
  const verifiedContributions = user.contributions.filter(c => c.status === 'verified');
  const verifiedCount = verifiedContributions.length;
  
  // Evolution logic: Core grows after 10 successful contributions
  const isEvolved = verifiedCount >= 10;
  
  // Distribute stars into orbits
  const orbits = [
    verifiedContributions.slice(0, 3),
    verifiedContributions.slice(3, 8),
    verifiedContributions.slice(8, 15),
    verifiedContributions.slice(15, 25), // Added a 4th orbit for high-impact users
  ];

  // Dynamic Sizing based on view and evolution
  const getSunSize = () => {
    if (isDetailView) {
      return isEvolved ? 'w-32 h-32' : 'w-24 h-24';
    }
    return isEvolved ? 'w-16 h-16' : 'w-12 h-12';
  };

  const orbitSizes = isDetailView 
    ? [120, 200, 280, 360] 
    : [60, 95, 130, 165];

  const sunSizeClass = getSunSize();

  return (
    <div className={`relative flex items-center justify-center ${isDetailView ? 'w-[800px] h-[800px]' : 'w-[350px] h-[350px]'}`}>
      
      {/* Central Identity (The Core) */}
      <div className="z-10 relative">
        {/* Enhanced Glow for Evolved Cores */}
        <div className={`absolute inset-0 bg-yellow-500/20 blur-3xl rounded-full ${isEvolved ? 'animate-pulse scale-150 opacity-100' : 'animate-pulse opacity-50'}`} />
        
        {isEvolved && (
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute inset-x-[-20%] inset-y-[-20%] border border-yellow-500/20 rounded-full border-dashed"
          />
        )}

        <motion.div 
          layout
          className={`${sunSizeClass} rounded-full bg-gradient-to-br from-yellow-100 via-yellow-400 to-orange-600 shadow-[0_0_60px_rgba(234,179,8,0.5)] flex items-center justify-center border-2 border-white/30 overflow-hidden relative`}
        >
          {/* Internal Texture/Shine */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.4),transparent)]" />
          
          <span className={`font-black text-slate-900 z-10 select-none ${isDetailView ? (isEvolved ? 'text-5xl' : 'text-4xl') : (isEvolved ? 'text-xl' : 'text-lg')}`}>
            {user.name.charAt(0)}
          </span>
        </motion.div>
        
        <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 whitespace-nowrap z-20">
          <div className="flex flex-col items-center gap-1">
            <span className={`text-white font-bold ${isDetailView ? 'text-sm' : 'text-[10px]'} bg-slate-900/90 px-3 py-1 rounded-full border border-slate-700 backdrop-blur-md shadow-xl`}>
              {user.name}
            </span>
            {isEvolved && (
              <span className="text-[10px] text-yellow-400 font-black uppercase tracking-widest animate-pulse">
                Evolved Entity
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Orbits */}
      {orbits.map((stars, idx) => (
        stars.length > 0 && (
          <div 
            key={idx} 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.03]"
            style={{ width: orbitSizes[idx] * 2, height: orbitSizes[idx] * 2 }}
          >
            <motion.div
              className="w-full h-full relative"
              animate={{ rotate: 360 }}
              transition={{ duration: 20 + (idx * 12), repeat: Infinity, ease: "linear" }}
            >
              {stars.map((star, sIdx) => {
                const angle = (360 / stars.length) * sIdx;
                const radius = orbitSizes[idx];
                return (
                  <div 
                    key={star.id} 
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                    style={{ transform: `rotate(${angle}deg) translate(${radius}px) rotate(-${angle}deg)` }}
                  >
                    <StarNode presetId={star.presetId} size={isDetailView ? 'md' : 'sm'} delay={sIdx * 0.1} />
                  </div>
                );
              })}
            </motion.div>
          </div>
        )
      ))}
    </div>
  );
};

export default SolarSystem;